import React, { useContext, useState } from "react";
import OwButton from "../../../shared/OwButton/OwButton";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import IErrorDTO from "../../../../models/DTO/IErrorDTO";
import INotification from "../../../../models/INotification";
import UserContext from "../../../../contexts/UserContext";
import SupplierContext from "../../../../contexts/SupplierContext";
import RestEndpointsEnum from "../../../../models/RestEndpointsEnum";
import { buildRestUrl, buildAuthHeadersForFetch } from "../../../../services/RestService";
import NotificationLevelEnum from "../../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../../models/NotificationPositionEnum";
import { INYSLABulkCreateAttributesResultDTO, INYSLACategoryResultDTO } from "../../../../models/DTO/INYSLAExportDTO";
import "./NYSLASetupAttributesModal.scss";

type Props = {
    closeModalFn: () => void;
    catalogGuid: string;
    catalogName: string;
    notifyFn: (data: IErrorDTO | null, _notification?: INotification, isFormatted?: boolean) => void;
};

export const NYSLASetupAttributesModal: React.FC<Props> = (props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [result, setResult] = useState<INYSLABulkCreateAttributesResultDTO | null>(null);
    const [showCategoryDetails, setShowCategoryDetails] = useState<boolean>(false);

    const closeModal = () => {
        props.closeModalFn();
    };

    const handleSetupAttributes = async () => {
        setIsProcessing(true);
        setResult(null);

        try {
            const url = buildRestUrl({
                endpoint: RestEndpointsEnum.WineNYSLAAttributesBulk,
                params: { catalogGuid: props.catalogGuid },
            });

            const headers = buildAuthHeadersForFetch({
                userModel,
                supplierModel,
            });

            const response = await fetch(url, {
                method: "POST",
                headers,
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            const data: INYSLABulkCreateAttributesResultDTO = await response.json();
            setResult(data);

            const notification: INotification = {
                message: "nysla_setup_attributes_complete",
                level: NotificationLevelEnum.success,
                position: NotificationPositionEnum.bc,
            };
            props.notifyFn(null, notification);
        } catch (error) {
            console.error("Error setting up NYSLA attributes:", error);

            const notification: INotification = {
                message: error instanceof Error ? error.message : "nysla_setup_attributes_failed",
                level: NotificationLevelEnum.error,
                position: NotificationPositionEnum.bc,
                skipMessageTranslate: error instanceof Error,
            };
            props.notifyFn(null, notification);
        } finally {
            setIsProcessing(false);
        }
    };

    const renderCategoryResult = (category: INYSLACategoryResultDTO, index: number) => {
        const statusClass = category.success ? "success" : "failed";
        return (
            <div key={index} className={`category-result p-2 ${statusClass}`}>
                <div className={"d-flex justify-content-between align-items-center"}>
                    <span className={"fw-bold"}>{category.categoryName}</span>
                    <span className={`badge ${category.success ? "bg-success" : "bg-danger"}`}>
                        {category.success ? `${category.createdCount} created` : "Failed"}
                    </span>
                </div>
                {category.skippedCount > 0 && <div className={"text-muted small"}>{category.skippedCount} skipped (already exist)</div>}
                {category.error && <div className={"text-danger small"}>{category.error}</div>}
            </div>
        );
    };

    return (
        <div className={"NYSLASetupAttributesModal p-3 w-100"}>
            <div className={"w-100"}>
                <div className={"d-flex justify-content-between align-items-center"}>
                    <OwTitle leftIconName={"fad fa-cogs"} title={"nysla_setup_attributes"} tag={"h3"} />
                    <div>
                        <OwButton className={"btn-danger"} centerIconClass={"fas fa-times"} fn={closeModal} />
                    </div>
                </div>
            </div>

            <div className={"mt-3"}>
                {!result && (
                    <>
                        <div className={"mb-3"}>
                            <OwLabel title={"catalog"} />
                            <div className={"fw-bold"}>{props.catalogName}</div>
                        </div>

                        <div className={"mb-3 text-muted"}>
                            This will create NY SLA attributes (Beverage Type, TTB ID, Combo Limit, Primary Info, NYS Product) for all
                            categories in this catalog that don&apos;t already have them.
                        </div>

                        <div className={"mb-3"}>
                            <OwButton
                                fn={handleSetupAttributes}
                                title={"setup_attributes"}
                                className={"btn-primary"}
                                active={isProcessing}
                                disableOnActive={true}
                                disabled={isProcessing}
                                leftIconClass={isProcessing ? "fas fa-spinner fa-spin" : "fas fa-cogs"}
                            />
                        </div>
                    </>
                )}

                {result && (
                    <div className={"setup-results mt-3 p-3 border rounded"}>
                        <OwTitle title={"results"} tag={"h5"} />

                        <div className={"d-flex gap-4 mt-2 flex-wrap"}>
                            <div>
                                <span className={"fw-bold"}>{result.totalCategories}</span>
                                <span className={"ms-1"}>categories processed</span>
                            </div>
                            <div>
                                <span className={"fw-bold text-success"}>{result.totalAttributesCreated}</span>
                                <span className={"ms-1"}>attributes created</span>
                            </div>
                            {result.totalAttributesSkipped > 0 && (
                                <div>
                                    <span className={"fw-bold text-warning"}>{result.totalAttributesSkipped}</span>
                                    <span className={"ms-1"}>already existed</span>
                                </div>
                            )}
                            {result.failedCategories > 0 && (
                                <div>
                                    <span className={"fw-bold text-danger"}>{result.failedCategories}</span>
                                    <span className={"ms-1"}>failed</span>
                                </div>
                            )}
                        </div>

                        {result.categoryResults.length > 0 && (
                            <div className={"mt-3"}>
                                <OwButton
                                    fn={() => setShowCategoryDetails(!showCategoryDetails)}
                                    title={showCategoryDetails ? "hide_details" : "show_details"}
                                    className={"btn-sm btn-outline-secondary"}
                                />
                                {showCategoryDetails && (
                                    <div className={"mt-2 category-results-list"}>
                                        {result.categoryResults.map((category, index) => renderCategoryResult(category, index))}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className={"mt-3"}>
                            <OwButton fn={closeModal} title={"close"} className={"btn-secondary"} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
